import axios from 'axios'
import { API_URL } from '../../../../modules/auth/core/_requests'

export interface BulkAnalysisRequest {
  order_ids: string[]
}

export interface BulkAnalysisResponse {
  success: boolean
  summary: {
    total_orders_analyzed: number
    full_fulfillment_orders: number
    partial_fulfillment_orders: number
    failed_orders: number
  }
  full_fulfillment: FullFulfillmentOrder[]
  partial_fulfillment: PartialFulfillmentOrder[]
  failed_orders: FailedOrder[]
}

// Analysis result interfaces
export interface FullFulfillmentOrder {
  order_id: string
  order_name: string
  total_items: number
  total_quantity_needed: number
  recommended_store: string
  store_code: string
  inventory_details: InventoryDetail[]
}

export interface PartialFulfillmentOrder {
  order_id: string
  order_name: string
  total_items: number
  total_quantity_needed: number
  store_assignments: StoreAssignment[]
}

export interface FailedOrder {
  order_id: string
  order_name: string
  total_items: number
  total_quantity_needed: number
  reason: string
  unfulfillable_items: UnfulfillableItem[]
}

export interface InventoryDetail {
  line_item_id: string
  product_sku: string
  product_name: string
  quantity_needed: number
  quantity_available: number
  has_enough: boolean
}

export interface StoreAssignment {
  store_name: string
  store_code: string
  line_item_ids: string[]
}

// Backend response structure for partial fulfillment (different from execution plan)
export interface BackendStoreAssignment {
  store_code: string
  store_name: string
  assigned_line_items: Array<{
    line_item_id: string
    product_sku: string
    product_name: string
    quantity_needed: number
    quantity_available: number
  }>
}

export interface UnfulfillableItem {
  line_item_id: string
  product_sku: string
  product_name: string
  quantity_needed: number
  max_available_across_stores: number
  shortage: number
}

// Execution plan interfaces
export interface ExecutionPlan {
  full_fulfillment_orders: FullFulfillmentExecutionOrder[]
  partial_fulfillment_orders: PartialFulfillmentExecutionOrder[]
  skipped_orders: string[]
}

export interface FullFulfillmentExecutionOrder {
  order_id: string
  store_assignment: {
    store_name: string
    line_item_ids: string[]
  }
}

export interface PartialFulfillmentExecutionOrder {
  order_id: string
  store_assignments: StoreAssignmentExecution[]
}

export interface StoreAssignmentExecution {
  store_name: string
  line_item_ids: string[]
}

// Execution response interfaces
export interface ExecutionResponse {
  success: boolean
  execution_summary: {
    total_orders_processed: number
    total_line_items_processed: number
    execution_start_time: string
    execution_end_time: string
    execution_duration_ms: number
    success_rate: {
      orders: string
      line_items: string
    }
  }
  detailed_results: {
    successful_orders: ExecutedOrder[]
    failed_orders: ExecutedOrder[]
    skipped_orders: SkippedOrder[]
  }
  store_assignment_summary: StoreAssignmentSummary[]
}

export interface ExecutedOrder {
  order_id: string
  order_name: string
  fulfillment_type: 'full' | 'partial'
  total_line_items: number
  assigned_stores: string[]
  line_item_assignments: LineItemAssignment[]
  order_status: string
  processing_time_ms: number
  error?: string
  failed_line_items?: FailedLineItem[]
}

export interface SkippedOrder {
  order_id: string
  order_name: string
  reason: string
  total_line_items: number
}

export interface LineItemAssignment {
  line_item_id: string
  product_name: string
  quantity: number
  assigned_store: string
  status: string
  execution_time: string
}

export interface FailedLineItem {
  line_item_id: string
  product_name: string
  error: string
  current_status: string
}

export interface StoreAssignmentSummary {
  store_name: string
  store_code: string
  orders_assigned: number
  line_items_assigned: number
  assignment_details: Array<{
    order_id: string
    line_items_count: number
  }>
}

export async function analyzeBulkOrders(orderIds: string[]): Promise<BulkAnalysisResponse> {
  try {
    const response = await axios.post(`${API_URL}/api/shopify-shipping/dashboard/orders-bulk-assignments/bulk-analyze`, {
      order_ids: orderIds
    })
    
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        throw new Error(error.response.data?.error || 'Failed to analyze bulk orders')
      } else {
        throw new Error('Network error when analyzing bulk orders')
      }
    }
    throw error
  }
}

export async function executeBulkAssignment(executionPlan: ExecutionPlan): Promise<ExecutionResponse> {
  try {
    const response = await axios.post(`${API_URL}/api/shopify-shipping/dashboard/orders-bulk-assignments/bulk-execute`, {
      execution_plan: executionPlan
    })
    
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        throw new Error(error.response.data?.error || 'Failed to execute bulk assignment')
      } else {
        throw new Error('Network error when executing bulk assignment')
      }
    }
    throw error
  }
} 